import React from 'react';

// Gedeelde, professionele factuur-/bon-weergave.
// Wordt gebruikt door PrintFactuur (echte PDF/print) én BonLayout (Live preview),
// zodat de preview een exacte representatie is van het uiteindelijke document.
// Alles met INLINE styles zodat de print-innerHTML 1-op-1 meekomt.

const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('nl-NL'); } catch { return String(d); }
};
const addDays = (d, days) => { const x = new Date(d); x.setDate(x.getDate() + days); return x; };

function statusBadge(bon, dueDate, t) {
  const type = bon.type || 'Bon';
  if (type === 'Offerte') return { label: t('OFFERTE', 'OFFERTE'), bg: '#f1f5f9', fg: '#475569' };
  if (type === 'Werkbon') return { label: t('WERKBON', 'WERKBON'), bg: '#eff6ff', fg: '#1d4ed8' };
  // Factuur
  const totaal = Number(bon.totaal) || 0;
  const open = Number(bon.openstaand) || 0;
  const betaald = Number(bon.betaald) || 0;
  if (totaal > 0 && open <= 0) return { label: t('BETAALD', 'BETAALD'), bg: '#dcfce7', fg: '#15803d' };
  if (dueDate && open > 0 && new Date() > dueDate) return { label: t('VERVALLEN', 'VERVALLEN'), bg: '#fee2e2', fg: '#b91c1c' };
  if (betaald > 0 && open > 0) return { label: t('DEELS BETAALD', 'DEELS BETAALD'), bg: '#fef3c7', fg: '#b45309' };
  return { label: t('OPENSTAAND', 'OPENSTAAND'), bg: '#eff6ff', fg: '#1d4ed8' };
}

export default function InvoiceDocument({ bon, klant, voertuig, gegevens, layout = {}, logoUrl, t = (a, b) => b || a }) {
  const accent = layout.kleur || '#2563eb';
  const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
  const hobby = !!gegevens?.hobbyMode || !!bon?.hobbyMode;
  const btwPct = bon?.btwPercentage ?? (hobby ? 0 : (gegevens?.btwPercentage ?? 21));
  const isFactuur = bon.type === 'Factuur';

  const termijn = Number(gegevens?.betaaltermijnDagen) || 14;
  const dueDate = bon.datum ? addDays(bon.datum, termijn) : null;
  const badge = statusBadge(bon, dueDate, t);
  const tenaamstelling = gegevens?.ibanNaam || gegevens?.tenaamstelling || gegevens?.naam || '';

  const v = voertuig || {};
  const vMerk = v.merk || bon.merk;
  const vModel = [v.model, v.uitvoering].filter(Boolean).join(' ') || bon.model;
  const vBouwjaar = v.bouwjaar;
  const vVin = v.chassisnummer || v.vin || bon.chassisnummer;
  const vApk = v.apkVervalt || v.vervaldatumApk;
  const kmStand = bon.kilometerstand || v.kmStand;

  const regels = (bon.regels || []).filter(r => (r.markering || 'Klant') !== 'Intern');
  const nogTeVoldoen = (Number(bon.totaal) || 0) - (Number(bon.betaald) || 0) - (Number(bon.aanbetaling) || 0);

  const S = {
    head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `3px solid ${accent}`, paddingBottom: 16, marginBottom: 18 },
    logoImg: { maxWidth: 150, maxHeight: 80, objectFit: 'contain' },
    logoTxt: { fontSize: 26, fontWeight: 800, color: accent, letterSpacing: 1.2 },
    company: { fontSize: 11.5, color: '#475569', marginTop: 6, lineHeight: 1.6 },
    doctype: { fontSize: 24, fontWeight: 800, color: '#0f172a', letterSpacing: 0.5 },
    badge: { display: 'inline-block', marginTop: 8, padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, background: badge.bg, color: badge.fg },
    infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, margin: '4px 0 20px' },
    card: { border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', background: '#fafbfc' },
    cardH: { fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: accent, marginBottom: 8 },
    row: { display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11.5, lineHeight: 1.7, color: '#334155' },
    rowLbl: { color: '#64748b' },
    rowVal: { fontWeight: 600, color: '#0f172a', textAlign: 'right' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 4 },
    th: { background: '#f1f5f9', borderBottom: `2px solid ${accent}`, padding: '9px 10px', textAlign: 'left', fontSize: 11, color: '#334155', fontWeight: 700 },
    thR: { textAlign: 'right' },
    td: { padding: '9px 10px', borderBottom: '1px solid #e8edf3', verticalAlign: 'top' },
    tdR: { textAlign: 'right', whiteSpace: 'nowrap' },
    totalsWrap: { display: 'flex', justifyContent: 'flex-end', marginTop: 14 },
    totalsBox: { width: 300, fontSize: 12.5 },
    tRow: { display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#475569' },
    tRowBig: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', marginTop: 4, borderTop: `2px solid ${accent}`, fontSize: 17, fontWeight: 800, color: '#0f172a' },
    payBox: { marginTop: 22, border: `1px solid ${accent}`, borderRadius: 10, overflow: 'hidden' },
    payHead: { background: accent, color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 800, letterSpacing: 0.4 },
    payBody: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 18px', padding: '12px 14px', fontSize: 12 },
    foot: { marginTop: 24, fontSize: 11.5, color: '#475569', lineHeight: 1.6 },
    bottom: { marginTop: 22, paddingTop: 12, borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: 10.5, color: '#64748b' },
  };

  const Info = ({ lbl, val }) => val ? (
    <div style={S.row}><span style={S.rowLbl}>{lbl}</span><span style={S.rowVal}>{val}</span></div>
  ) : null;

  return (
    <div style={{ color: '#0f172a', fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif' }}>
      {/* HEADER */}
      <div style={S.head}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          {logoUrl ? <img src={logoUrl} alt="Logo" style={S.logoImg} data-testid="inv-logo" />
            : <div style={S.logoTxt}>{layout.logoTekst || gegevens?.naam || 'MONTEURCENTRAAL'}</div>}
          <div style={S.company}>
            {logoUrl && <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a', marginBottom: 3 }}>{gegevens?.naam}</div>}
            {gegevens?.adres}<br />
            {gegevens?.postcode} {gegevens?.plaats}<br />
            {gegevens?.telefoon && <>Tel: {gegevens.telefoon}<br /></>}
            {gegevens?.email}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={S.doctype}>{isFactuur && layout.titel ? layout.titel : (bon.type || 'BON').toUpperCase()}</div>
          <div style={S.badge} data-testid="inv-status-badge">{badge.label}</div>
        </div>
      </div>

      {/* INFO BAND: Klant | Voertuig | Gegevens */}
      <div style={S.infoGrid}>
        <div style={S.card} data-testid="inv-klant">
          <div style={S.cardH}>{t('Factuur aan', 'Factuur aan')}</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
            <b>{klant?.naam || '-'}</b><br />
            {klant?.adres}<br />
            {klant?.postcode} {klant?.plaats}
            {klant?.email && <><br />{klant.email}</>}
            {klant?.telefoon && <><br />{klant.telefoon}</>}
          </div>
        </div>

        <div style={S.card} data-testid="inv-voertuig">
          <div style={S.cardH}>{t('Voertuiggegevens', 'Voertuiggegevens')}</div>
          <Info lbl={t('Kenteken')} val={bon.kenteken ? <b style={{ fontFamily: 'ui-monospace, monospace' }}>{bon.kenteken}</b> : null} />
          <Info lbl={t('Merk', 'Merk')} val={vMerk} />
          <Info lbl={t('Model', 'Model')} val={vModel} />
          <Info lbl={t('Bouwjaar', 'Bouwjaar')} val={vBouwjaar} />
          <Info lbl={t('Kilometerstand', 'Kilometerstand')} val={kmStand ? `${Number(kmStand).toLocaleString('nl-NL')} km` : null} />
          <Info lbl={t('VIN', 'VIN')} val={vVin ? <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10.5 }}>{vVin}</span> : null} />
          {!bon.kenteken && !vMerk && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{t('Geen voertuig gekoppeld', 'Geen voertuig gekoppeld')}</div>}
        </div>

        <div style={S.card} data-testid="inv-gegevens">
          <div style={S.cardH}>{t('Gegevens', 'Gegevens')}</div>
          <Info lbl={t('Factuurnummer', 'Factuurnummer')} val={<b>{bon.nummer}</b>} />
          <Info lbl={t('Datum')} val={fmtDate(bon.datum)} />
          {isFactuur && <Info lbl={t('Vervaldatum', 'Vervaldatum')} val={fmtDate(dueDate)} />}
          {bon.werkorderNummer && <Info lbl={t('Werkorder', 'Werkorder')} val={bon.werkorderNummer} />}
          {bon.monteur && <Info lbl={t('Monteur', 'Monteur')} val={bon.monteur} />}
          {bon.werkdatum && <Info lbl={t('Werkdatum', 'Werkdatum')} val={fmtDate(bon.werkdatum)} />}
          {bon.referentie && <Info lbl={t('Referentie', 'Referentie')} val={bon.referentie} />}
          {bon.creditVan && <Info lbl={t('Creditfactuur op', 'Creditfactuur op')} val={bon.creditVan} />}
        </div>
      </div>

      {/* LINE ITEMS */}
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>{t('Omschrijving')}</th>
            <th style={{ ...S.th, ...S.thR, width: 60 }}>{t('Aantal')}</th>
            <th style={{ ...S.th, ...S.thR, width: 100 }}>{t('Stukprijs', 'Stukprijs')}</th>
            <th style={{ ...S.th, ...S.thR, width: 100 }}>{t('Totaal')}</th>
          </tr>
        </thead>
        <tbody>
          {regels.map((r, i) => {
            const garantie = (r.markering || 'Klant') === 'Garantie';
            return (
              <tr key={i}>
                <td style={S.td}>
                  {r.code && <b style={{ fontFamily: 'ui-monospace, monospace', color: accent }}>{r.code} </b>}
                  {r.omschr}
                  {r.type && r.type !== 'Onderdeel' && <span style={{ color: '#94a3b8', fontSize: 10.5 }}> · {t(r.type, r.type)}</span>}
                  {garantie && <i style={{ color: '#b45309' }}> ({t('garantie', 'garantie')})</i>}
                </td>
                <td style={{ ...S.td, ...S.tdR }}>{r.aantal}</td>
                <td style={{ ...S.td, ...S.tdR }}>{garantie ? eur(0) : eur(r.prijs)}</td>
                <td style={{ ...S.td, ...S.tdR, fontWeight: 600 }}>{garantie ? eur(0) : eur((r.aantal || 1) * (r.prijs || 0))}</td>
              </tr>
            );
          })}
          {regels.length === 0 && <tr><td colSpan={4} style={{ ...S.td, textAlign: 'center', color: '#94a3b8' }}>({t('geen regels', 'geen regels')})</td></tr>}
        </tbody>
      </table>

      {/* TOTALS */}
      <div style={S.totalsWrap}>
        <div style={S.totalsBox}>
          {bon.subtotaal != null && <div style={S.tRow}><span>{t('Subtotaal')}</span><span>{eur(bon.subtotaal)}</span></div>}
          {bon.kortingBedrag > 0 && (
            <div style={{ ...S.tRow, color: '#dc2626' }}>
              <span>{t('Korting', 'Korting')} {bon.kortingType === 'percentage' ? `(${bon.kortingWaarde}%)` : ''}</span>
              <span>− {eur(bon.kortingBedrag)}</span>
            </div>
          )}
          {hobby
            ? <div style={{ ...S.tRow, fontStyle: 'italic', color: '#b45309' }}><span>{t('Geen BTW (hobby/particulier)', 'Geen BTW (hobby/particulier)')}</span><span>—</span></div>
            : (bon.btw != null && <div style={S.tRow}><span>{t('BTW')} {btwPct}%</span><span>{eur(bon.btw)}</span></div>)}
          <div style={S.tRowBig} data-testid="inv-totaal"><span>{t('Totaal')}</span><span>{eur(bon.totaal)}</span></div>
          {bon.betaald > 0 && <div style={S.tRow}><span>{t('Reeds betaald', 'Reeds betaald')}</span><span>− {eur(bon.betaald)}</span></div>}
          {bon.aanbetaling > 0 && <div style={S.tRow}><span>{t('Aanbetaling', 'Aanbetaling')}</span><span>− {eur(bon.aanbetaling)}</span></div>}
          {bon.aanbetaling > 0
            ? <div style={{ ...S.tRow, fontWeight: 800, color: '#0f172a', fontSize: 14 }}><span>{t('Nog te voldoen', 'Nog te voldoen')}</span><span>{eur(nogTeVoldoen)}</span></div>
            : (bon.openstaand > 0 && <div style={{ ...S.tRow, fontWeight: 800, color: '#b91c1c', fontSize: 14 }}><span>{t('Openstaand')}</span><span>{eur(bon.openstaand)}</span></div>)}
        </div>
      </div>

      {/* PAYMENT BOX (alleen factuur met openstaand bedrag) */}
      {isFactuur && (bon.openstaand > 0 || nogTeVoldoen > 0) && (
        <div style={S.payBox} data-testid="inv-payment-box">
          <div style={S.payHead}>{t('Betaalinformatie', 'Betaalinformatie')}</div>
          <div style={S.payBody}>
            <div><span style={{ color: '#64748b' }}>IBAN</span><br /><b style={{ fontFamily: 'ui-monospace, monospace' }}>{gegevens?.iban || '—'}</b></div>
            <div><span style={{ color: '#64748b' }}>{t('Tenaamstelling', 'Tenaamstelling')}</span><br /><b>{tenaamstelling || '—'}</b></div>
            <div><span style={{ color: '#64748b' }}>{t('Betalingskenmerk', 'Betalingskenmerk')}</span><br /><b>{bon.nummer}</b></div>
            <div><span style={{ color: '#64748b' }}>{t('Vervaldatum', 'Vervaldatum')}</span><br /><b>{fmtDate(dueDate)}</b></div>
          </div>
        </div>
      )}

      {/* SERVICE / RETENTION FOOTER */}
      {(vApk || bon.aanbevolenOnderhoud || bon.notitie) && (
        <div style={{ ...S.card, marginTop: 18 }} data-testid="inv-service-footer">
          <div style={S.cardH}>{t('Service & onderhoud', 'Service & onderhoud')}</div>
          {vApk && <Info lbl={t('Volgende APK', 'Volgende APK')} val={fmtDate(vApk)} />}
          {bon.aanbevolenOnderhoud && <Info lbl={t('Aanbevolen onderhoud', 'Aanbevolen onderhoud')} val={bon.aanbevolenOnderhoud} />}
          {bon.notitie && <div style={{ fontSize: 11.5, color: '#475569', marginTop: 6 }}>{bon.notitie}</div>}
        </div>
      )}

      {/* FOOTER */}
      <div style={S.foot}>
        {hobby && <p style={{ fontStyle: 'italic', color: '#b45309' }}>{t('Deze bon is opgesteld in hobby-/particulier-modus. Er wordt geen BTW in rekening gebracht.', 'Deze bon is opgesteld in hobby-/particulier-modus. Er wordt geen BTW in rekening gebracht.')}</p>}
        <p>{layout.voettekst || `${t('Bedankt voor uw vertrouwen in', 'Bedankt voor uw vertrouwen in')} ${gegevens?.naam || 'MonteurCentraal'}.`}</p>
        <div style={S.bottom}>
          {gegevens?.kvk && <>KvK: {gegevens.kvk}{!hobby && gegevens?.btw ? ' · ' : ''}</>}
          {!hobby && gegevens?.btw && <>BTW: {gegevens.btw} · </>}
          {gegevens?.iban && <>IBAN: {gegevens.iban}</>}
        </div>
      </div>
    </div>
  );
}
